// Дорожня карта СМКОР: що робити платнику зараз — за станом із кабінету (критерії ризиковості, зупинені ПН),
// причинами зупинення з квитанцій і лімітом СЕА. Текст — за docs/recommendations-format.md: напрям, нумеровані кроки
// з датами, умова наприкінці; норми й підстави — окремо, у basis (рівень «Показати підстави»).
//
// Норми (звірено з текстами на zakon.rada.gov.ua, 14.10.2026):
// - ПКМУ № 1165 (зі змінами № 1048 від 26.08.2025), Порядок зупинення: п. 6 — рішення про ризиковість (дод. 4), для п. 8
//   у рішенні операції, період, код товару, контрагент; інформація й копії документів — комісія розглядає 7 робочих днів,
//   не розглянула — платника виключають; п. 6-1 — після виключення ПН, зупинені через ризиковість, реєструються
//   автоматично наступного робочого дня (подані не раніше 180 днів до подання інформації, без рішення комісії щодо них,
//   операції не відповідають критеріям ризиковості операцій, Рмакс, достатня сума в СЕА); пп. 12–16 — таблиця даних,
//   5 робочих днів. Порядок розгляду скарг: п. 3 — 10 робочих днів після рішення; п. 11-1 — 10 (30 з відеоконференцією)
//   календарних днів.
// - Наказ Мінфіну № 520 (зі змінами № 527 від 22.10.2024): п. 6 — пояснення до 365 календарних днів після дати складання
//   ПН, кілька ПН одним поясненням (один покупець і договір або однакові коди); п. 9 — рішення 5 робочих днів, запит
//   додаткових документів — відповідь 5 робочих днів.
// Форми ДПС (xsd_all): J1314901 — інформація щодо невідповідності критеріям ризиковості; J1316002 — скарга на рішення про
// ризиковість; J1312603 — пояснення до зупинених ПН; J1360102 — копії документів; J1313205 — скарга на відмову в
// реєстрації ПН; J1412603 — рішення про ризиковість (вхідне).

import { stopOutcome } from "./stop-outcome.js";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
// Чи діє статус ризикового платника на дату: є критерій без дати закінчення або з датою закінчення не раніше today.
// risk — рядки risk_criteria кабінету; null — даних немає (невідомо). Рядок, що вже не діє, статусу не дає
export function riskyNow(risk, today) {
  if (!Array.isArray(risk)) return null;
  return risk.some((r) => { const end = r?.dateEnd ?? r?.DATEEND; return !end || String(end).slice(0, 10) >= today; });
}

export const dmy = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join(".") : "");
const date = (s) => new Date(`${String(s).slice(0, 10)}T00:00:00Z`);
const money = (v) => Number(v || 0).toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/ /g, " ");

// Робочі дні: понеділок–п'ятниця. Під час воєнного стану святкові дні не є вихідними, тож окремого календаря свят немає
export function addWorkDays(start, n) {
  const d = date(start);
  let left = n;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const w = d.getUTCDay();
    if (w !== 0 && w !== 6) left--;
  }
  return iso(d);
}
export function addDays(start, n) { const d = date(start); d.setUTCDate(d.getUTCDate() + n); return iso(d); }

// Причина зупинення з тексту квитанції ДПС
export function stopReason(text) {
  const t = String(text || "");
  if (/критері\S* ризиковості платника/i.test(t)) return "payer";
  if (/критері\S* ризиковості здійснення операц/i.test(t)) return "operation";
  if (/таблиц/i.test(t)) return "table";
  return null;
}

const pnLabel = (list) => {
  const nums = list.map((s) => Number(s.nmr)).filter(Number.isFinite).sort((a, b) => a - b);
  if (!nums.length) return "";
  const runs = [];
  for (const n of nums) { const r = runs.at(-1); if (r && n === r[1] + 1) r[1] = n; else runs.push([n, n]); }
  return runs.map(([a, b]) => (a === b ? `№ ${a}` : b === a + 1 ? `№ ${a}, ${b}` : `№ ${a}–${b}`)).join(", ");
};

const ym = (d) => String(d).slice(0, 7);
const shift = (m, k) => { const [y, mm] = m.split("-").map(Number); const d = new Date(Date.UTC(y, mm - 1 + k, 1)); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`; };

// Умова Рмакс п. 6-1 для автоматичної реєстрації: у кожному з 6 місяців перед поданням інформації (J1314901), у місяці подання
// й у місяці автореєстрації ПДВ у ПН/РК, прийнятих для реєстрації (за місяцем прийняття), не більший за Рмакс того місяця.
// Рмакс = Рнайб × 1,4; Рнайб — найбільший місячний ПДВ зареєстрованих ПН/РК за операціями 12 періодів до місяця прийняття,
// за які минув строк декларації. Вікно беремо до попереднього місяця включно (найширше), тож висновки — консервативні.
// monthly — { accepted: { "YYYY-MM": ПДВ прийнятих }, registered: { "YYYY-MM": ПДВ зареєстрованих за місяцем операції }, from: "YYYY-MM" };
// dp — показники кабінету (PM, PM_CACL_FROM, PM_CACL_TO)
export function pmaxCheck({ monthly, dp, fileMonth }) {
  if (!monthly?.accepted) return null;
  const pmFrom = dp?.PM_CACL_FROM ? ym(dp.PM_CACL_FROM) : null, pmTo = dp?.PM_CACL_TO ? ym(dp.PM_CACL_TO) : null; // період кабінету — 12 місяців включно (09.2025–08.2026)
  const pm = dp?.PM != null ? Number(dp.PM) : null;
  const months = [];
  for (let k = -6; k <= 0; k++) months.push(shift(fileMonth, k));
  // Дві межі Рнайб у вікні 12 місяців до місяця прийняття: верхня (невідомий місяць — без межі; місяць у періоді показника
  // кабінету — не більший за його Рнайб) і нижня (точні суми з даних eCabinet; Рнайб кабінету — якщо весь його період у вікні).
  // «Перевищує» — лише понад верхню межу, «виконується» — лише в межах нижньої; інакше «невідомо»
  const inPm = (w) => pm != null && pmFrom && pmTo && w >= pmFrom && w <= pmTo;
  const exactOf = (w) => (monthly.from && w >= monthly.from ? Number(monthly.registered?.[w] || 0) : null);
  const rows = months.map((m) => {
    const accepted = Number(monthly.accepted[m] || 0);
    // Верхня межа: найширше вікно M−12…M−1 (декларацію за M−1 подано з 20-го числа M); місяць у періоді кабінету — не більший за його Рнайб
    let hi = 0, bounded = true;
    for (let k = -12; k <= -1; k++) {
      const w = shift(m, k), e = exactOf(w);
      if (inPm(w)) hi = Math.max(hi, pm, e ?? 0);
      else if (e != null) hi = Math.max(hi, e);
      else bounded = false;
    }
    // Нижня межа: найвужче вікно M−13…M−2 (строк декларації за M−2 минув до початку M) — точні суми; Рнайб кабінету, якщо весь його період у вікні
    let lo = 0;
    const narrow = [];
    for (let k = -13; k <= -2; k++) narrow.push(shift(m, k));
    for (const w of narrow) { const e = exactOf(w); if (e != null) lo = Math.max(lo, e); }
    if (pm != null && pmFrom && pmTo && narrow[0] <= pmFrom && pmTo <= narrow.at(-1)) lo = Math.max(lo, pm);
    const pmax = Math.round(hi * 1.4 * 100) / 100, pmaxLow = Math.round(lo * 1.4 * 100) / 100;
    const verdict = bounded && accepted > pmax ? "over" : accepted <= pmaxLow ? "ok" : "unknown";
    return { month: m, accepted, pmax: bounded ? pmax : null, pmaxLow, verdict };
  });
  const over = rows.filter((r) => r.verdict === "over");
  return { months: rows, over, ok: !over.length && rows.every((r) => r.verdict === "ok") };
}
const MONTH_NAMES = ["січні", "лютому", "березні", "квітні", "травні", "червні", "липні", "серпні", "вересні", "жовтні", "листопаді", "грудні"];
const inMonth = (m) => `у ${MONTH_NAMES[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}`;

// risk — рядки risk_criteria (typ, typName, dateBegin, dateEnd); stopped — smkor_stop; reasons — { [ijcode|nmr]: "payer"|… };
// sea — { limit, at } (сума, на яку можна зареєструвати ПН, з останньої операції СЕА); today — YYYY-MM-DD
// notice — прийняте повідомлення J1314901 { acceptedAt: YYYY-MM-DD } (якщо є): крок «подайте» стає «прийнято, рішення до …»
// practice — dpsPractice (lib/compliance/dps-practice.js): що комісія вимагає на практиці й пакет документів з даних компанії
export function smkorPlan({ risk, stopped, reasons = {}, sea = null, today, monthly = null, dp = null, notice = null, practice = null }) {
  const sections = [], basis = [];
  const activeRisk = Array.isArray(risk) ? risk.filter((r) => !r.dateEnd || String(r.dateEnd).slice(0, 10) >= today) : null;
  const open = Array.isArray(stopped) ? stopped.filter((s) => Number(s.status) === 1) : null;
  const explained = (s) => (s.det || []).some((d) => d.hdate);
  const decided = (s) => (s.det || []).some((d) => d.actd);
  const waiting = open ? open.filter((s) => !explained(s) && !decided(s)) : [];
  // Запит комісії про доповнення (J1407802) без відповіді — окремо від «на розгляді»: є строк 5 робочих днів
  const asked = (s) => (s.det || []).some((d) => d.datePovOut && !d.datePovIn && !d.actd);
  const askedList = open ? open.filter((s) => explained(s) && !decided(s) && asked(s)) : [];
  const inReview = open ? open.filter((s) => explained(s) && !decided(s) && !asked(s)) : [];
  const reasonOf = (s) => reasons[s.ijcode] || reasons[`nmr:${s.nmr}:${String(s.crtdate).slice(0, 10)}`] || null;

  if (activeRisk === null && open === null) return { status: "unknown", sections, basis };
  const pmax = pmaxCheck({ monthly, dp, fileMonth: ym(today) });
  const autoDead = Boolean(pmax?.over.length);

  if (activeRisk?.length) {
    const r = [...activeRisk].sort((a, b) => String(b.dateBegin).localeCompare(String(a.dateBegin)))[0];
    const decisionDay = String(r.dateBegin).slice(0, 10);
    const p8 = activeRisk.some((x) => Number(x.typ) === 10 || /п\.?\s*8\b/.test(String(x.typName)));
    const complaintBy = addWorkDays(decisionDay, 10);
    const reviewBy = addWorkDays(today, 7);
    sections.push({
      key: "risk",
      title: "Зняти статус ризикового платника",
      why: `З ${dmy(decisionDay)} компанію віднесено до ризикових${p8 ? " за п. 8 (податкова інформація про ризикові операції)" : ""}: поки статус діє, кожна ваша ПН зупинятиметься.`,
      steps: [
        { text: `Відкрийте рішення комісії від ${dmy(decisionDay)} у «Листуванні з ДПС» → Вхідні і випишіть операції, через які вас визнали ризиковим: контрагент, період, код товару чи послуги, придбання чи постачання.`, link: "correspondence" },
        practice?.pack?.length
          ? { text: "На кожну таку операцію і за пакетом нижче («Що попросить комісія») зберіть документи: комісія на практиці просить не лише операції з рішення, а й діяльність загалом.", link: "primary" }
          : { text: "На кожну таку операцію зберіть договір, видаткові накладні або акти, ТТН, рахунки, банківські виписки, сертифікати, а також докази ресурсів: працівники, склад, транспорт, оренда.", link: "primary" },
        ...(autoDead ? [] : [{ text: "До подання J1314901 відправте на реєстрацію всі вже складені ПН: після зняття статусу автоматично зареєструються лише ті, що зупинені до дня його подання.", link: "erpn" }]),
        notice && notice.acceptedAt >= decisionDay
          ? { text: `Повідомлення J1314901 прийнято ДПС ${dmy(notice.acceptedAt)}. Комісія має розглянути його за 7 робочих днів — до ${dmy(addWorkDays(notice.acceptedAt, 7))}; не розгляне вчасно — статус знімуть автоматично. Рішення прийде в «Листування з ДПС».`, due: addWorkDays(notice.acceptedAt, 7), link: "correspondence" }
          : { text: `Подайте повідомлення J1314901 з копіями документів (J1360102). Комісія має розглянути його за 7 робочих днів — якщо подати сьогодні, до ${dmy(reviewBy)}; не розгляне вчасно — статус знімуть автоматично.`, link: "smkor/notice" },
        { text: `Скаргу J1316002 на рішення від ${dmy(decisionDay)} можна подати до ${dmy(complaintBy)} включно; подання J1314901 цей строк не зупиняє й не продовжує. Якщо комісія за J1314901 статус залишить, на її нове рішення — свої 10 робочих днів. Скаргу розглядають 10 календарних днів.`, due: complaintBy },
      ],
      until: p8 ? "Якщо в рішенні не вказано жодної операції чи контрагента, пояснювати нічого — одразу подавайте скаргу або йдіть до суду." : "Якщо підстава статусу — не операції, а дані реєстрації (адреса, керівник, звітність), спершу виправте їх, а тоді подавайте J1314901.",
    });
  }

  // Пакет і вимоги комісії понад Порядок — з реальної практики (п. 8): документи з даних компанії, а ще навантаження, люди, запас
  if (activeRisk?.length && practice?.pack?.length) {
    const hot = (practice.items || []).filter((x) => x.status === "bad" || x.status === "warn");
    sections.push({
      key: "dps-pack",
      title: "Що попросить комісія",
      why: "Так виглядає реальний перелік вимог комісії до ризикового платника за п. 8: документи на всю діяльність, а не лише на операції з рішення. Зберіть їх одним пакетом до J1314901.",
      steps: practice.pack.map((text) => ({ text, link: /20-ОПП/.test(text) ? "opp" : /МД/.test(text) ? "customs" : "primary" })),
      until: null,
    });
    if (hot.length) sections.push({
      key: "dps-demands",
      title: "Що комісія вимагає понад документи",
      why: "Цих вимог немає в Порядку № 1165, але комісія ставить їх як умову зняття статусу. Ваші дані показують, де саме вона натисне.",
      steps: hot.map((x) => ({ text: `${x.title}. ${x.now}${x.todo?.[0] ? ` ${x.todo[0]}` : ""}`, warn: x.status === "bad" })),
      until: "Продаж, уточнююча декларація чи найм людей мають бути реальними, з документами. Фіктивна операція заради зняття статусу — ризик уже для вас; вимоги, що виходять за Порядок, просіть викласти письмово.",
    });
  }

  // Без квитанції: ПН, подану до дня рішення про ризиковість, зупинено з іншої причини (і п. 6-1 на неї не поширюється)
  const decisionDay = activeRisk?.length ? activeRisk.map((r) => String(r.dateBegin).slice(0, 10)).sort()[0] : null;
  const payerStopped = waiting.filter((s) => reasonOf(s) === "payer"
    || (decisionDay && !reasonOf(s) && !(s.impdate && String(s.impdate).slice(0, 10) < decisionDay)));
  const otherStopped = waiting.filter((s) => !payerStopped.includes(s));

  if (payerStopped.length) {
    const vat = payerStopped.reduce((a, s) => a + Number(s.vat || 0), 0);
    const first = [...payerStopped].sort((a, b) => String(a.crtdate).localeCompare(String(b.crtdate)))[0];
    const lastDay = addDays(String(first.crtdate).slice(0, 10), 365);
    const firstImp = payerStopped.map((s) => String(s.impdate || "").slice(0, 10)).filter(Boolean).sort()[0];
    const window180 = firstImp ? addDays(firstImp, 180) : null;
    const buyers = [...new Set(payerStopped.map((s) => s.cpTin).filter(Boolean))];
    const seaShort = sea?.limit != null && Number(sea.limit) < vat;
    const pm = pmaxCheck({ monthly, dp, fileMonth: ym(today) });
    const notify = { text: `Повідомте ${buyers.length > 1 ? `кожному з ${buyers.length} покупців` : "покупцю"}, яку ПН зупинено, суму ПДВ і обраний шлях: податковий кредит з'явиться лише після реєстрації, дату не обіцяйте.` };
    const explain = (lead) => ({ text: `${lead}пояснення J1312603 з копіями документів саме за операцією кожної ПН. Одним поясненням можна охопити кілька ПН одного покупця за одним договором або з однаковими кодами товару. Останній строк для найранішої ПН — ${dmy(lastDay)}; не залишайте на останні дні.`, due: lastDay, link: "primary" });
    const extra = { text: "Комісія відповідає за 5 робочих днів. Якщо попросить додаткові документи, у вас 5 робочих днів на відповідь — цей строк не пропускайте за жодної тактики." };
    if (pm?.over.length) {
      const list = pm.over.map((r) => `${inMonth(r.month)} — ${money(r.accepted)} при Рмакс не більше ${money(r.pmax)}`).join("; ");
      sections.push({
        key: "stopped-payer",
        title: `Зупинені ПН ${pnLabel(payerStopped)} — ПДВ ${money(vat)}`,
        why: `Їх зупинено через статус платника, але автоматично після зняття статусу вони, найімовірніше, не зареєструються: ПДВ у ПН, прийнятих на реєстрацію, перевищує Рмакс (${list}), а ця умова має виконуватись у кожному з 6 місяців перед поданням J1314901 і в місяці подання.`,
        steps: [
          { text: "Не чекайте зняття статусу заради автоматичної реєстрації цих ПН — розблоковуйте їх поясненнями паралельно з роботою над статусом." },
          explain("Подайте "),
          extra,
          notify,
          { text: "Перевірте Рмакс і суми за місяцями в кабінеті ДПС («СМКОР» → показники) — якщо там інші цифри, напишіть нам." },
        ],
        warn: true,
        until: (() => {
          const lastOver = pm.over.map((r) => r.month).sort().at(-1), reopen = `${shift(lastOver, 7)}-01`;
          const tail = window180 && reopen <= window180
            ? ` Теоретично автоматичний шлях відкриється лише при поданні J1314901 з ${dmy(reopen)} до ${dmy(window180)}, якщо статус протримається до того часу, — заради цього чекати недоцільно.`
            : "";
          return `Порада зміниться, якщо кабінет покаже вищий Рнайб, ніж у даних eCabinet, або ПН у цих місяцях насправді прийнято на реєстрацію менше.${tail}`;
        })(),
      });
    } else {
      sections.push({
        key: "stopped-payer",
        title: `Зупинені ПН ${pnLabel(payerStopped)} — ПДВ ${money(vat)}`,
        why: "Їх зупинено через статус платника, тож після зняття статусу вони можуть зареєструватися автоматично наступного робочого дня — якщо одночасно виконуються всі умови нижче.",
        steps: [
          { text: "Обрана тактика — поки комісія розглядає статус, пояснень до цих ПН не подавати: рішення комісії по ПН (будь-яке) знімає її з автоматичної реєстрації. Це вибір, а не заборона." },
          { text: `J1314901 подайте не пізніше ${window180 ? dmy(window180) : "180 днів від подання ПН"}: ПН, подані більш ніж за 180 днів до нього, автоматично не реєструються.`, due: window180 },
          { text: `${pm?.ok ? "Рмакс за останні 6 місяців і поточний виконується за даними eCabinet" : "Рмакс за останні 6 місяців і поточний перевірити не вдалося"} — ПДВ у ПН, прийнятих на реєстрацію в кожному місяці, має бути не більший за Рмакс; не подавайте в місяці подання J1314901 і автореєстрації ПН понад Рмакс.` , warn: !pm?.ok },
          { text: `У день автоматичної реєстрації в СЕА має бути сума для реєстрації не менша за ${money(vat)} плюс нові ПН у черзі${sea?.limit != null ? ` (зараз ${money(sea.limit)}${sea.at ? ` на ${dmy(sea.at)}` : ""}${seaShort ? ` — бракує ${money(vat - Number(sea.limit))}` : ""})` : ""}.`, link: "sea", warn: seaShort },
          notify,
          explain("Якщо статус не знімуть або після зняття ПН лишиться зупиненою, подайте "),
          extra,
        ],
        until: "Автоматично не зареєструються й ПН, у яких сама операція відповідає критеріям ризиковості операцій на день автореєстрації.",
      });
    }
  }

  if (otherStopped.length) {
    const vat = otherStopped.reduce((a, s) => a + Number(s.vat || 0), 0);
    const first = [...otherStopped].sort((a, b) => String(a.crtdate).localeCompare(String(b.crtdate)))[0];
    const lastDay = addDays(String(first.crtdate).slice(0, 10), 365);
    sections.push({
      key: "stopped-operation",
      title: `Розблокувати ПН ${pnLabel(otherStopped)} — ПДВ ${money(vat)}`,
      why: activeRisk?.length ? "Ці ПН зупинено ще до статусу ризикового платника, тож після його зняття самі вони не зареєструються — потрібні пояснення й документи." : "Ці ПН зупинено через самі операції: самі вони не зареєструються, потрібні пояснення й документи.",
      steps: [
        { text: "Відкрийте квитанцію про зупинення кожної ПН і подивіться код товару чи послуги, через який її зупинено: пояснюйте лише цю операцію." },
        { text: `Подайте пояснення J1312603 з копіями документів (J1360102): договір, первинні документи, ТТН, банківські виписки. Останній строк для найранішої ПН — ${dmy(lastDay)}.`, due: lastDay, link: "primary" },
        { text: "Комісія відповідає за 5 робочих днів. Якщо попросить додаткові документи, у вас 5 робочих днів на відповідь." },
        { text: "Якщо відмовить, подайте скаргу J1313205 протягом 10 робочих днів від рішення або йдіть до суду." },
        { text: "Щоб такі ПН не зупинялись надалі, подайте таблицю даних платника з кодами товарів на вході й виході — комісія розглядає її 5 робочих днів." },
      ],
      until: "Поки компанія — ризиковий платник, таблиця даних не допоможе: ПН зупинятимуться незалежно від неї.",
    });
  }

  if (askedList.length) {
    const dues = askedList.map((s) => stopOutcome(s, today)).filter((o) => o?.due).map((o) => o.due).sort();
    sections.push({
      key: "asked",
      title: `Комісія запитала доповнення: ПН ${pnLabel(askedList)}`,
      why: "Без доповнення комісія відмовить у реєстрації протягом 5 робочих днів після строку (п. 9 Порядку № 520).",
      steps: [
        { text: "Прочитайте запит у «Листуванні з ДПС»: які документи позначено й що в «Додатковій інформації».", link: "correspondence" },
        { text: `Подайте доповнення формою J1307801 (не J1312603 — друге пояснення до тієї самої ПН ДПС не приймає) з повним текстом і лише новими документами${dues[0] ? `; останній строк — ${dmy(dues[0])}` : ""}.`, due: dues[0] || null },
      ],
    });
  }

  if (inReview.length) {
    sections.push({
      key: "in-review",
      title: `На розгляді комісії: ПН ${pnLabel(inReview)}`,
      why: "Пояснення подано, рішення ще немає.",
      steps: [
        { text: "Комісія має відповісти за 5 робочих днів після отримання пояснень — перевіряйте вхідні в «Листуванні з ДПС».", link: "correspondence" },
        { text: "Якщо надійде запит додаткових документів, відповідайте протягом 5 робочих днів." },
      ],
      until: "Після відмови нові пояснення до тієї самої ПН не приймаються — лише скарга або суд.",
    });
  }

  if (activeRisk?.length) {
    sections.push({
      key: "new-pn",
      title: "Нові ПН, поки діє статус",
      why: "Статус зупиняє всі ПН, які ви подаєте, незалежно від позитивної історії й таблиці даних.",
      steps: [
        { text: "Подавайте нові ПН у звичайні строки — вони зупинятимуться до зняття статусу." },
        { text: "ПН, подані після J1314901, автоматично не зареєструються: якщо статус знімуть, а вони лишаться зупиненими, подайте до них пояснення J1312603." },
        { text: "Таблицю даних подавайте після зняття статусу: зараз вона на зупинення не вплине." },
      ],
      until: null,
    });
  }

  basis.push(
    "Рішення про ризиковість, інформація та копії документів, 7 робочих днів, автоматичне виключення — п. 6 Порядку зупинення реєстрації ПН/РК (ПКМУ № 1165 від 11.12.2019, зі змінами від 26.08.2025).",
    "Автоматична реєстрація ПН після зняття статусу — п. 6-1 того самого Порядку: ПН, зупинені з дати включення до переліку ризикових до дня подання інформації, подані не раніше 180 днів до подання інформації; немає рішення комісії щодо них; операції не відповідають критеріям ризиковості операцій; сума ПДВ у кожному з 6 попередніх місяців не перевищує Рмакс; достатня сума в СЕА.",
    "Рмакс = Рнайб × 1,4; Рнайб — найбільший місячний ПДВ зареєстрованих ПН/РК за операціями 12 періодів, що передують місяцю прийняття ПН, за які минув строк подання декларації (пп. 3 п. 2-1 Порядку зупинення). eCabinet рахує нижню й верхню межі Рмакс за Рнайб із показників кабінету та зареєстрованими ПН за місяцями; «невідомо» — коли меж недостатньо для висновку.",
    "Скарга — п. 3 Порядку розгляду скарги (10 робочих днів після рішення), розгляд — п. 11-1 (10 календарних днів, 30 — з відеоконференцією).",
    "Пояснення до зупинених ПН — пп. 5–6 Порядку, затвердженого наказом Мінфіну № 520 від 12.12.2019 (365 календарних днів після дати складання ПН); рішення — п. 9 (5 робочих днів; запит додаткових документів — 5 робочих днів на відповідь).",
    "Таблиця даних платника — пп. 12–16 Порядку зупинення (розгляд 5 робочих днів).",
    "Робочі дні — понеділок–п'ятниця: під час воєнного стану святкові дні не є вихідними.",
    ...(practice?.pack?.length ? ["«Що попросить комісія» і «Що комісія вимагає понад документи» — з реального переліку вимог комісії до ризикового платника за п. 8 (жовтень 2026): пояснення про діяльність, штатний розклад, документи 20-ОПП, документи з найбільшими покупцями й постачальниками, імпорт, виписки з відміткою банку; податкове навантаження, працівники (5 осіб, 12 000 грн), продаж запасу. Це практика, а не норма Порядку № 1165."] : []),
    "Форми ДПС: J1314901 — інформація щодо невідповідності критеріям ризиковості; J1316002 — скарга на рішення про ризиковість; J1312603 — пояснення до зупинених ПН; J1360102 — копії документів; J1313205 — скарга на відмову в реєстрації ПН.",
  );
  const status = activeRisk?.length ? "risky" : waiting.length || inReview.length || askedList.length ? "stopped" : "ok";
  return { status, sections, basis, pmax };
}
